'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import type { PurchaseOrder } from '@/lib/schemas';
import { CheckCircle, AlertCircle } from 'lucide-react';

interface ReceivingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseOrder: PurchaseOrder | null;
  onReceive: (poId: string, receiveData: { items: Array<{ po_item_id: string; quantity_received: number; batch_number?: string; expiry_date?: string }> }) => void;
}

export function ReceivingModal({ open, onOpenChange, purchaseOrder, onReceive }: ReceivingModalProps) {
  const [receivedQuantities, setReceivedQuantities] = useState<Record<string, number>>({});
  const [batchNumbers, setBatchNumbers] = useState<Record<string, string>>({});
  const [expiryDates, setExpiryDates] = useState<Record<string, string>>({});

  // Reset state when modal opens with new PO
  useEffect(() => {
    if (open && purchaseOrder) {
      const initialReceived: Record<string, number> = {};
      purchaseOrder.items.forEach(item => {
        initialReceived[item.item_id] = item.quantity_received || 0;
      });
      setReceivedQuantities(initialReceived);
      setBatchNumbers({});
      setExpiryDates({});
    }
  }, [open, purchaseOrder]);

  const handleQuantityChange = (itemId: string, value: number) => {
    const maxQty = purchaseOrder?.items.find(i => i.item_id === itemId)?.quantity_ordered || 0;
    setReceivedQuantities(prev => ({
      ...prev,
      [itemId]: Math.min(Math.max(0, value), maxQty)
    }));
  };

  const getTotalReceived = () => {
    if (!purchaseOrder) return 0;
    return purchaseOrder.items.reduce((sum, item) => {
      const received = receivedQuantities[item.item_id] || 0;
      return sum + received;
    }, 0);
  };

  const getTotalOrdered = () => {
    return purchaseOrder?.items.reduce((sum, item) => sum + item.quantity_ordered, 0) || 0;
  };

  const getAllItemsFullyReceived = () => {
    if (!purchaseOrder) return false;
    return purchaseOrder.items.every(item => {
      const received = receivedQuantities[item.item_id] || 0;
      return received >= item.quantity_ordered;
    });
  };

  const handleReceive = () => {
    if (!purchaseOrder) return;

    const receiveItems = {
      items: purchaseOrder.items.map(item => ({
        po_item_id: item.item_id,
        quantity_received: (receivedQuantities[item.item_id] || 0) - (item.quantity_received || 0),
        batch_number: batchNumbers[item.item_id] || undefined,
        expiry_date: expiryDates[item.item_id] || undefined,
      }))
    };

    onReceive(purchaseOrder.po_id!, receiveItems);
    onOpenChange(false);
  };

  // Build title with status info
  const buildTitle = () => {
    if (!purchaseOrder) return 'Receive Purchase Order';
    const totalOrdered = getTotalOrdered();
    const alreadyReceived = getTotalReceived();
    const remaining = totalOrdered - alreadyReceived;
    return `Receive PO: ${purchaseOrder.po_number} (${remaining} remaining)`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{buildTitle()}</DialogTitle>
        </DialogHeader>

        {purchaseOrder && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Total Ordered</p>
                <p className="text-2xl font-bold">{getTotalOrdered()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">To Receive</p>
                <p className="text-2xl font-bold text-blue-600">{getTotalOrdered() - getTotalReceived()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Already Received</p>
                <p className="text-2xl font-bold text-green-600">{getTotalReceived()}</p>
              </div>
            </div>

            {/* Items */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {purchaseOrder.items.map(item => {
                const orderedQty = item.quantity_ordered;
                const prevReceived = item.quantity_received || 0;
                const currentReceived = receivedQuantities[item.item_id] || prevReceived;
                const remainingQty = orderedQty - prevReceived;
                const toReceiveNow = currentReceived - prevReceived;

                const isFullyReceived = currentReceived >= orderedQty;
                const isValidQty = toReceiveNow >= 0 && toReceiveNow <= remainingQty;

                return (
                  <div key={item.item_id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{item.item_name || `Item ${item.item_id}`}</p>
                        <p className="text-sm text-gray-600">
                          {item.item_code || ''} • UOM: {item.unit_of_measure || ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">
                          Ordered: <span className="font-semibold">{orderedQty}</span>
                        </p>
                        <p className="text-sm text-gray-600">
                          Prev Received: <span className="font-semibold">{prevReceived}</span>
                        </p>
                        <p className={`text-sm font-semibold ${isFullyReceived ? 'text-green-600' : 'text-blue-600'}`}>
                          Now: {currentReceived}
                        </p>
                      </div>
                    </div>

                    {/* Quantity to receive now */}
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium mb-1">
                          Quantity to Receive Now
                        </label>
                        <Input
                          type="number"
                          min={0}
                          max={remainingQty}
                          value={toReceiveNow}
                          onChange={(e) => handleQuantityChange(item.item_id, prevReceived + parseInt(e.target.value) || 0)}
                          disabled={remainingQty === 0}
                          className={!isValidQty && toReceiveNow > 0 ? 'border-red-500' : ''}
                        />
                        {remainingQty === 0 && (
                          <p className="text-xs text-green-600 mt-1">
                            Fully received
                          </p>
                        )}
                        {!isValidQty && toReceiveNow > 0 && (
                          <p className="text-xs text-red-600 mt-1">
                            Cannot receive more than remaining ({remainingQty})
                          </p>
                        )}
                      </div>

                      {/* Batch & Expiry for traceable items */}
                      {purchaseOrder.traceability_level !== 'none' && (
                        <>
                          <div className="flex-1">
                            <label className="block text-sm font-medium mb-1">
                              Batch Number (optional)
                            </label>
                            <Input
                              value={batchNumbers[item.item_id] || ''}
                              onChange={(e) => setBatchNumbers(prev => ({
                                ...prev,
                                [item.item_id]: e.target.value
                              }))}
                              placeholder="Batch #"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-sm font-medium mb-1">
                              Expiry Date (optional)
                            </label>
                            <Input
                              type="date"
                              value={expiryDates[item.item_id] || ''}
                              onChange={(e) => setExpiryDates(prev => ({
                                ...prev,
                                [item.item_id]: e.target.value
                              }))}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between border-t pt-4">
              <div>
                {getAllItemsFullyReceived() && (
                  <Badge variant="success" className="gap-1">
                    <CheckCircle className="h-4 w-4" />
                    All items fully received
                  </Badge>
                )}
                {!getAllItemsFullyReceived() && getTotalReceived() > 0 && (
                  <Badge variant="warning" className="gap-1">
                    <AlertCircle className="h-4 w-4" />
                    Partial receive
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleReceive}
                  disabled={getTotalReceived() <= getTotalOrdered() && getAllItemsFullyReceived()}
                >
                  Complete Receiving
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
